import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCopy, FiLink, FiSearch, FiX } from 'react-icons/fi';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import Pagination from '../../components/ui/Pagination';
import SortableHeader from '../../components/ui/SortableHeader';
import EmptyState from '../../components/ui/EmptyState';
import Modal from '../../components/ui/Modal';
import { Button, Input } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import ApplicationsSectionTabs from './ApplicationsSectionTabs';
import {
  getActiveDirectoryUsers,
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';

const PAGE_SIZE = 25;

const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-amber-500' },
  { value: 'submitted', label: 'Submitted', color: 'bg-blue-500' },
  { value: 'reviewed', label: 'Reviewed', color: 'bg-green-500' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-400' },
];

function StatusBadge({ status }) {
  const s = STATUSES.find((x) => x.value === status);
  if (!s) return <span className="text-xs text-gray-400">{status || '—'}</span>;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${s.color}`}>
      {s.label}
    </span>
  );
}

function formatDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function leadName(lead) {
  if (!lead) return null;
  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim();
  return name || null;
}

export default function PreUnderwritingLogsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { users: directoryUsers } = useUsersDirectory();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const [linkOpen, setLinkOpen] = useState(false);
  const [agencySlug, setAgencySlug] = useState('');
  const [linkAgentId, setLinkAgentId] = useState(user?.id || '');
  const [linkLoading, setLinkLoading] = useState(false);

  const isManagerPlus = ['admin', 'super_admin', 'manager'].includes(user?.role);
  const hasActiveFilters = search || filterStatus || filterAgent;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.preUnderwritingLogs.getAll({
        status: filterStatus || undefined,
        agentId: filterAgent || undefined,
        search: search || undefined,
      });
      setLogs(data || []);
      setPage(1);
    } catch {
      addToast({ type: 'error', message: 'Failed to load pre-underwriting logs' });
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterAgent, search, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const agents = useMemo(() => {
    if (!isManagerPlus) return [];
    return getActiveDirectoryUsers(directoryUsers).map((u) => ({
      id: u.id,
      name: u.full_name || 'Unknown',
    }));
  }, [isManagerPlus, directoryUsers]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'created_at' ? 'desc' : 'asc');
    }
  };

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...logs].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'agent') {
        aVal = resolveDisplayName(a.agent_id, a.agent) || '';
        bVal = resolveDisplayName(b.agent_id, b.agent) || '';
      }
      if (sortField === 'lead') {
        aVal = leadName(a.lead) || '';
        bVal = leadName(b.lead) || '';
      }
      if (aVal == null || aVal === '') return 1;
      if (bVal == null || bVal === '') return -1;
      if (typeof aVal === 'string') return aVal.localeCompare(bVal) * dir;
      return (aVal > bVal ? 1 : aVal < bVal ? -1 : 0) * dir;
    });
  }, [logs, sortField, sortDir, directoryUsers]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openPublicLinkModal = async () => {
    setLinkOpen(true);
    setLinkAgentId(user?.id || '');
    if (!user?.agency_id) return;
    setLinkLoading(true);
    try {
      const agency = await api.agencies.getById(user.agency_id);
      setAgencySlug(agency?.slug || '');
    } catch (e) {
      addToast({ type: 'error', message: e.message || 'Could not load agency link' });
    } finally {
      setLinkLoading(false);
    }
  };

  const publicUrl =
    agencySlug && linkAgentId
      ? api.preUnderwritingLogs.buildPublicLink(agencySlug, linkAgentId)
      : '';

  const copyPublicLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      addToast({ type: 'success', message: 'Public link copied' });
    } catch {
      addToast({ type: 'error', message: 'Could not copy link' });
    }
  };

  const sortProps = { sortField, sortDir, onSort: handleSort };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Pre-Underwriting Application Logs</h1>
          <p className="text-gray-400 text-sm mt-1">
            {logs.length} log{logs.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ApplicationsSectionTabs />
          <button
            type="button"
            onClick={openPublicLinkModal}
            className="flex items-center gap-2 px-4 py-2 border border-gray-700
                       hover:border-gray-500 text-gray-300 hover:text-white
                       text-sm font-medium rounded-lg transition-colors"
          >
            <FiLink className="w-4 h-4" />
            Get Public Link
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by full name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg
                       text-sm text-white placeholder-gray-500 focus:outline-none
                       focus:border-[#00AAFF] transition-colors"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm
                     text-gray-300 focus:outline-none focus:border-[#00AAFF] transition-colors"
        >
          <option value="">All Statuses</option>
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        {isManagerPlus && (
          <select
            value={filterAgent}
            onChange={(e) => setFilterAgent(e.target.value)}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm
                       text-gray-300 focus:outline-none focus:border-[#00AAFF] transition-colors"
          >
            <option value="">All Agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        )}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setFilterStatus('');
              setFilterAgent('');
            }}
            className="px-3 py-2 text-xs text-gray-400 hover:text-white
                       border border-gray-700 rounded-lg transition-colors
                       flex items-center gap-1.5"
          >
            <FiX className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-500 text-sm">
            <Spinner className="text-primary" />
            Loading logs…
          </div>
        ) : sorted.length === 0 ? (
          <EmptyState
            title="No pre-underwriting logs"
            description={
              hasActiveFilters
                ? 'Try adjusting your filters.'
                : 'Public form submissions will appear here.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <SortableHeader label="Full Name" field="full_name" {...sortProps} />
                    <SortableHeader label="Agent" field="agent" {...sortProps} />
                    <SortableHeader label="Status" field="status" {...sortProps} />
                    <SortableHeader label="Submitted" field="created_at" {...sortProps} />
                    <SortableHeader label="Lead" field="lead" {...sortProps} />
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((log) => (
                    <tr
                      key={log.id}
                      onClick={() => navigate(`/applications/pre-underwriting/${log.id}`)}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {log.full_name || '—'}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {resolveDisplayName(log.agent_id, log.agent) || '—'}
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={log.status} />
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-xs">
                        {formatDate(log.created_at)}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {leadName(log.lead) || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sorted.length > PAGE_SIZE && (
              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={sorted.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={linkOpen}
        onClose={() => setLinkOpen(false)}
        title="Get Public Link"
        className="max-w-lg"
      >
        {linkLoading ? (
          <div className="flex items-center justify-center py-10 gap-2 text-gray-500 text-sm">
            <Spinner className="text-primary" />
            Loading…
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Share this link so applicants can submit a pre-underwriting application for the selected agent.
            </p>
            {isManagerPlus && agents.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Agent</label>
                <select
                  value={linkAgentId}
                  onChange={(e) => setLinkAgentId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Public URL</label>
              <Input value={publicUrl} readOnly className="font-mono text-xs" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setLinkOpen(false)}>Close</Button>
              <Button onClick={copyPublicLink} disabled={!publicUrl}>
                <FiCopy className="w-4 h-4 mr-1.5" />
                Copy Link
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
